<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import type { Route, Stop } from '@/@types/gtfs-ressource';
import type { OnDemandRoute } from '@/@types/on-demand-route';
import { isOnDemandRoute } from '@/libs/on-demand-helper';
import { useAppStore } from '@/stores/app';
import { useGtfsStore } from '@/stores/gtfs';

const appStore = useAppStore();
const gtfsStore = useGtfsStore();
const { t } = useI18n();
// One per row of every list: a plain element and the native animation API keep
// the rows cheap, where an animation library instance in each made long lists slow.
const iconEl = ref<HTMLElement>();

const props = defineProps<{
  element: Route | Stop | OnDemandRoute;
}>();

const bookmarked = computed<boolean>(() => {
  if (isOnDemandRoute(props.element)) {
    return appStore.favorites.onDemandRoutes?.[props.element.route_id] || false;
  }
  if ('route_id' in props.element) {
    return appStore.favorites.routes?.[props.element.route_id] || false;
  }
  if ('stop_id' in props.element) {
    return appStore.favorites.stops?.[gtfsStore.getCanonicalStopId(props.element.stop_id)] || false;
  }

  return false;
});

// The bookmark pops and swings once, like a tag being pinned on. Adding only:
// removing is a quiet action.
function pop() {
  if (!iconEl.value?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  iconEl.value.animate(
    [
      { transform: 'scale(1) rotate(0deg)' },
      { transform: 'scale(1.45) rotate(-14deg)' },
      { transform: 'scale(0.9) rotate(10deg)' },
      { transform: 'scale(1.1) rotate(-4deg)' },
      { transform: 'scale(1) rotate(0deg)' },
    ],
    { duration: 550, easing: 'ease-out' },
  );
}

function switchState() {
  if (!bookmarked.value) {
    if (isOnDemandRoute(props.element)) appStore.starOnDemandRoute(props.element.route_id);
    else if ('route_id' in props.element) appStore.starRoute(props.element.route_id);
    else if ('stop_id' in props.element) appStore.starStop(props.element.stop_id);
    pop();
    appStore.showToast(t('favorites.added'), 'bookmark');
  } else {
    if (isOnDemandRoute(props.element)) appStore.unstarOnDemandRoute(props.element.route_id);
    else if ('route_id' in props.element) appStore.unstarRoute(props.element.route_id);
    else if ('stop_id' in props.element) appStore.unstarStop(props.element.stop_id);
  }
}
</script>

<template>
  <button
    class="bookmark-button__click-target"
    type="button"
    :aria-pressed="bookmarked"
    :aria-label="t('favorites.label')"
    @click.prevent="switchState()"
  >
    <span ref="iconEl" class="bookmark-button__icon">
      <BaseIcon name="bookmark" class="bookmark-button" :class="{ 'bookmark-button--active': bookmarked }" />
    </span>
  </button>
</template>

<style lang="scss" scoped>
.bookmark-button {
  color: $color-neutral-highest;
  font-size: 16px;
  transition: fill 0.15s ease-out;

  &--active {
    fill: $color-primary-highter;
    color: $color-primary-highter;
  }

  &__click-target {
    align-items: center;
    background: none;
    border: 0;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    padding: 10px;
    position: relative;
    transition: transform 0.1s;

    &:active {
      transform: scale(0.8);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;

      &:active {
        transform: none;
      }
    }

    // Widens the tap target past the icon's visual bounds without affecting the layout.
    &::after {
      content: '';
      inset: -8px;
      position: absolute;
    }
  }

  &__icon {
    display: inline-flex;
    // The swing pivots on the bookmark's top edge, where it would hang from.
    transform-origin: 50% 15%;
  }
}
</style>
